import { uniqBy } from 'lodash';
import { FormSectionHandler } from '../entity.handler';
import { ClientResponse, ClientUpdateRequest, VendorResponse, IdNameState } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField, IdNameComboValueHandlers } from 'app/shared/components/form-builder';
import { ComboField, EMPTY_GUID, EntityUtils } from '../utils';
import { nullAccessDenied } from 'app/services';

interface EditorValues {
    emailProvider?: IdNameState;
}

export class ClientNotificationsSectionHandler implements FormSectionHandler<ClientResponse, EditorValues> {

    type = 'form';
    icon = 'at';
    bulk = true;
    readonly = this.admin.user.owner.type === 'client';

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string
    ) {
    }

    private properties = ['emailProvider'];

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.CLIENT.NOTIFICATIONS.${key}`, values);
    }

    applyUpdate(entity: ClientResponse, payload: ClientUpdateRequest): Promise<ClientResponse> {
        return this.admin.applyUpdate(api => api.accounts.updateClient(entity.id, payload));
    }

    mapEntityToEditor(entity: ClientResponse): Promise<EditorValues> {
        const result = EntityUtils.mergeFields<EditorValues>(this.properties, entity, {});
        return Promise.resolve(result);
    }

    mapEditorToEntity(entity: ClientUpdateRequest, values: EditorValues): Promise<any> {
        const result = EntityUtils.mergeFields<EditorValues>(this.properties, entity, values);
        return Promise.resolve(result);
    }

    async getFields(entity: ClientResponse): Promise<FormBuilderField[]> {
        const vendor = this.readonly ? null : await this.admin.getCached(`getVendor/${this.ownerId}`, api => api.accounts.getVendor(this.ownerId)).catch(nullAccessDenied);

        return Promise.all([
            this.getEmailProviders(entity, vendor),
        ]).then(([emailProviderValues]) => {
            return [
                ComboField('emailProvider', this.translate('FIELDS.EMAILPROVIDER'), true, emailProviderValues, IdNameComboValueHandlers),      
            ];
        }).then(fields => EntityUtils.prepareBulk(!entity, ['emailProvider'], fields));
    }

    async getEmailProviders(client: ClientResponse, vendor: VendorResponse): Promise<{ key: string, value: string }[]> {
        const emailProviders = vendor && await this.admin.getCached(`listEmailProviders/${vendor.id}/0/100/name/state=active`, api => api.entities.listEmailProviders(vendor.id, 0, 100, 'name', 'state=active'));

        const result = [
            { id: EMPTY_GUID, name: 'Default', state: 'active' },
            ...(vendor?.availableEmailProviders || []),
            ...(emailProviders?.items || []),
        ];

        if (client && client.emailProvider && !result.find(emailProvider => emailProvider.id === client.emailProvider.id)) {
            result.unshift({
                id: client.emailProvider.id,
                name: client.emailProvider.name,
                state: client.emailProvider.state === 'active' ? 'unavailable' : client.emailProvider.state,
            } as any);
        }

        return uniqBy(result, 'id').map(x => {
            if (x.state && x.state !== 'active') {
                const translationKey = `STATES.${x.state.toUpperCase()}`;
                return { key: x.id, value: `${x.name} (${this.admin.translate(translationKey)})` };
            }

            return { key: x.id, value: x.name };
        }).sort((a, b) => a.value.localeCompare(b.value));
    }

}
